import { useNavigate, useParams } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useProjectTasks, ProjectTask } from '../hooks/useProjectTasks';
import { ProjectTasksFilters } from './ProjectTasksFilters';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { TablePagination } from '@/components/ui/TablePagination';
import { CardList } from '@/components/ui/CardList';

export function ProjectTasksList() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { tasks, isLoading } = useProjectTasks();

  return (
    <div className="space-y-6">
      {/* Bộ lọc */}
      <ProjectTasksFilters />

      {/* Danh sách */}
      <div className="bg-white rounded-md border border-slate-100  overflow-hidden">
        <div className="p-4 border-b border-slate-50 flex items-center justify-between">
        <h3 className="text-sm font-normal text-slate-900 tracking-wider">
            Danh sách công việc ({tasks.length})
          </h3>
          <div className="flex items-center gap-3">
             <button 
                onClick={() => navigate('/work-orders/new')}
                className="h-9 px-4 rounded-md bg-emerald-600 text-white flex items-center gap-2 text-xs lg:text-sm font-normal hover:bg-emerald-700 transition-all  shadow-emerald-100 active:scale-95"
              >
                <i className="ri-add-line" />
                <span>Thêm công việc</span>
             </button>
             <button className="w-9 h-9 flex items-center justify-center rounded-md border border-slate-200 text-slate-400 hover:text-slate-600 transition-all hover:bg-slate-50">
                <i className="ri-settings-3-line" />
             </button>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 text-xs font-normal text-slate-400 border-b border-slate-100">
                <th className="py-3 px-4 w-10 font-normal">
                  <input type="checkbox" className="rounded border-slate-200 text-emerald-600 focus:ring-emerald-500" />
                </th>
                <th className="py-3 px-4 w-10 text-center font-normal">#</th>
                <th className="py-3 px-4 font-normal">Tên công việc</th>
                <th className="py-3 px-4 font-normal">Người phụ trách</th>
                <th className="py-3 px-4 font-normal">Hạn hoàn thành</th>
                <th className="py-3 px-4 text-center font-normal">Độ ưu tiên</th>
                <th className="py-3 px-4 text-center font-normal">Tiến độ</th>
                <th className="py-3 px-4 text-center font-normal">Trạng thái</th>
                <th className="py-3 px-4 w-10 font-normal"></th>
              </tr>
            </thead>
            <tbody className="text-xs lg:text-sm divide-y divide-slate-50">
              {tasks.map((task) => (
                <tr key={task.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="py-4 px-4">
                    <input type="checkbox" className="rounded border-slate-200 text-emerald-600 focus:ring-emerald-500" />
                  </td>
                  <td className="py-4 px-4 text-slate-400 font-normal">{task.index}</td>
                  <td className="py-4 px-4">
                    <div className="flex flex-col">
                      <span className="font-normal text-slate-700">{task.name}</span>
                      <span className="text-xs text-slate-400">{task.location}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <img src={task.assignee.avatar} alt="" className="w-6 h-6 rounded-full border border-slate-100" />
                      <span className="font-normal text-slate-700 truncate group-hover:text-emerald-600 transition-colors cursor-pointer">{task.assignee.name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex flex-col text-xs font-normal text-slate-500">
                      <span>{task.startDate}</span>
                      <span>- {task.endDate}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={cn("px-2 py-0.5 rounded-md text-xs font-normal", task.priorityColor)}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3 min-w-[100px]">
                      <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                        <div 
                          className={cn(
                            "h-full transition-all duration-700", 
                            task.progress === 100 ? "bg-emerald-500" : task.progress > 0 ? "bg-blue-500" : "bg-slate-300"
                          )} 
                          style={{ width: `${task.progress}%` }} 
                        />
                      </div>
                      <span className="py-4 px-4 text-center font-normal text-slate-700">{task.progress}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={cn("px-2 py-0.5 rounded-md text-xs font-normal whitespace-nowrap", task.statusColor)}>
                      {task.status}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all" title="Xem chi tiết">
                        <i className="ri-eye-line" />
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all" title="Chỉnh sửa">
                        <i className="ri-edit-line" />
                      </button>
                      <ActionMenu 
                        items={[
                          { label: 'Giao việc', icon: 'ri-user-add-line', onClick: () => {} },
                          { label: 'Sao chép', icon: 'ri-file-copy-line', onClick: () => {} },
                          { label: 'Xóa', icon: 'ri-delete-bin-line', destructive: true, onClick: () => {} }
                        ]} 
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card List */}
        <div className="md:hidden p-4">
          <CardList 
            items={tasks}
            isLoading={isLoading}
            renderCard={(task) => (
              <div key={task.id} className="bg-white p-3 rounded-md border border-slate-100 space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-normal text-slate-400">#{task.index}</span>
                    <h4 className="text-xs font-normal text-slate-900">{task.name}</h4>
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="h-8 px-3 rounded-md border border-slate-100 sm:text-sm text-xs font-normal text-emerald-600 hover:bg-emerald-50 transition-all">
                      Chi tiết
                    </button>
                    <ActionMenu 
                      items={[
                        { label: 'Chỉnh sửa', icon: 'ri-edit-line', onClick: () => {} },
                        { label: 'Xóa', icon: 'ri-delete-bin-line', destructive: true, onClick: () => {} }
                      ]} 
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="space-y-1">
                    <p className="text-slate-400 font-normal">Hạng mục</p>
                    <span className={cn("px-2 py-0.5 rounded-md font-normal", task.categoryColor)}>{task.category}</span>
                  </div>
                  <div className="space-y-1 text-right">
                    <p className="text-slate-400 font-normal">Độ ưu tiên</p>
                    <span className={cn("px-2 py-0.5 rounded-md text-xs font-normal", task.priorityColor)}>{task.priority}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-slate-50/50 p-2 rounded-md">
                 <div className="flex items-center gap-1">
                    <img src={task.assignee.avatar} alt="" className="w-4 h-4 rounded-full" />
                    <span className="text-xs font-normal text-slate-600">{task.assignee.name}</span>
                 </div>
                 <span className={cn("px-2 py-0.5 rounded-md text-xs font-normal", task.statusColor)}>{task.status}</span>
                </div>
              </div>
            )}
          />
        </div>

        {/* Phân trang */}
        <div className="px-4 border-t border-slate-50 bg-slate-50/30">
          <TablePagination 
            currentPage={1} 
            totalItems={tasks.length} 
            pageSize={10} 
            onPageChange={() => {}} 
            itemLabel="công việc"
          />
        </div>
      </div>
    </div>
  );
}
